import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RoleBasedContent from '../components/RoleBasedContent';
import { 
  ChartBarIcon, 
  CogIcon, 
  UserGroupIcon, 
  ShieldCheckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const UserDashboard = () => {
  const { isAuthenticated, userRole, isLoading, sessionInfo } = useAuth();
  const navigate = useNavigate();

  // Security check and page title setup
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to home if not authenticated (will trigger login gate)
      navigate('/', { replace: true });
      return;
    }

    if (userRole) {
      // Update page title based on role
      const roleTitles = {
        founder: 'Founder Dashboard | Valeon',
        investor: 'Investor Portal | Valeon', 
        client: 'Client Dashboard | Valeon'
      };
      document.title = roleTitles[userRole] || 'Dashboard | Valeon';
    }
  }, [isAuthenticated, userRole, isLoading, navigate]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-accent-blue/30 border-t-accent-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cream-200">Loading Dashboard...</p>
        </motion.div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated || !userRole) {
    return null;
  }

  const getRoleInfo = () => {
    switch (userRole) {
      case 'founder':
        return {
          title: 'Founder Dashboard',
          subtitle: 'Complete platform oversight and control',
          icon: CogIcon,
          gradient: 'from-accent-gold via-yellow-500 to-accent-gold',
          iconBg: 'from-accent-gold to-yellow-500'
        };
      case 'investor':
        return {
          title: 'Investor Portal',
          subtitle: 'Advanced analytics and market insights',
          icon: ChartBarIcon,
          gradient: 'from-accent-blue via-blue-500 to-accent-blue',
          iconBg: 'from-accent-blue to-blue-500'
        };
      case 'client':
        return {
          title: 'Client Dashboard',
          subtitle: 'Personalized liquidity consulting and support',
          icon: UserGroupIcon,
          gradient: 'from-green-400 via-emerald-500 to-green-400',
          iconBg: 'from-green-400 to-emerald-500'
        };
      default:
        return {
          title: 'Dashboard',
          subtitle: 'Welcome to your platform',
          icon: ShieldCheckIcon,
          gradient: 'from-cream-200 via-white to-cream-200',
          iconBg: 'from-gray-400 to-gray-600'
        };
    }
  };

  const roleInfo = getRoleInfo();
  const IconComponent = roleInfo.icon;

  const getSessionTimeFormatted = () => {
    if (!sessionInfo) return 'Unknown';
    
    const sessionStart = new Date(sessionInfo.startTime);
    return sessionStart.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-primary-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-800 to-primary-900"></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-accent-blue/20 to-accent-gold/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-accent-gold/20 to-accent-blue/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              {/* Role Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-8"
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${roleInfo.iconBg} rounded-2xl flex items-center justify-center mx-auto shadow-2xl`}>
                  <IconComponent className="h-10 w-10 text-white" />
                </div>
              </motion.div>

              {/* Welcome Message */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-heading-xl font-serif font-bold text-cream-100 mb-4"
              >
                Welcome to Your{' '}
                <span className={`bg-gradient-to-r ${roleInfo.gradient} bg-clip-text text-transparent`}>
                  {roleInfo.title}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-body-lg text-cream-200 max-w-3xl mx-auto leading-relaxed"
              >
                {roleInfo.subtitle}. Your access level and available features are 
                customized based on your role within the Valeon ecosystem.
              </motion.p>

              {/* Session Info */}
              {sessionInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="mt-8 flex items-center justify-center space-x-6 text-cream-300 text-sm"
                >
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-4 w-4" />
                    <span>Session started: {getSessionTimeFormatted()}</span>
                  </div>
                  <div className="hidden sm:block w-1 h-1 bg-cream-300 rounded-full"></div>
                  <div className="flex items-center space-x-2">
                    <ShieldCheckIcon className="h-4 w-4" />
                    <span>Role: {userRole.charAt(0).toUpperCase() + userRole.slice(1)}</span>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <section className="py-20 bg-gradient-to-b from-primary-800 to-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <RoleBasedContent />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="py-16 bg-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-center"
            >
              <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-12">
                Quick Actions
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {/* Navigation shortcuts based on role */}
                <motion.a
                  href="/analytics"
                  whileHover={{ scale: 1.02, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  className="glass-card p-6 text-center hover:bg-white/10 transition-all duration-300 group"
                >
                  <ChartBarIcon className="h-8 w-8 text-accent-blue mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-cream-100 font-medium mb-2">Analytics</h3>
                  <p className="text-cream-300 text-sm">View detailed analytics and insights</p>
                </motion.a>

                <motion.a
                  href="/services"
                  whileHover={{ scale: 1.02, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  className="glass-card p-6 text-center hover:bg-white/10 transition-all duration-300 group"
                >
                  <UserGroupIcon className="h-8 w-8 text-accent-gold mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-cream-100 font-medium mb-2">Services</h3>
                  <p className="text-cream-300 text-sm">Explore our consulting services</p>
                </motion.a>

                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.02, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  className="glass-card p-6 text-center hover:bg-white/10 transition-all duration-300 group"
                >
                  <ShieldCheckIcon className="h-8 w-8 text-green-400 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-cream-100 font-medium mb-2">Support</h3>
                  <p className="text-cream-300 text-sm">Get help and contact support</p>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserDashboard;